import React, { useCallback, useEffect, useMemo } from 'react'
import { Box, CircularProgress, Grid, Typography } from '@mui/material'
import GenericAutocomplete from '../../../../../components/FormElements/FormAutoComplete'
import MultiSelectWithCheckBox from '../../../../../components/FormElements/MultiSelectWithCheckBox'
import GenericButton from '../../../../../components/GenericButton'
import AddIcon from '@mui/icons-material/Add'
import { ButtonSize, ButtonType } from '../../../../../components/GenericButton/styles'
import { type IAttribute } from '../../../../../models/Attributes'
import { ReactComponent as DeleteIcon } from '../../../../../assets/trash.svg'
import { shade } from '../../../../../colors'
import { useQuery } from '@tanstack/react-query'
import api from '../../../../../utils/api'
import { useFieldArray, useFormContext } from 'react-hook-form'
import { type IAttribute as TemplateAttributesType } from '../../../../../store/slices/SurveyTemplateSlice'

const operations = [
  { title: 'Is Not', value: 'is not' },
  { title: 'Is', value: 'is' }
]

interface AttributesFilterProps {
  attributes?: TemplateAttributesType[]
  initialLabel?: string
}

const AttributesFilter: React.FC<AttributesFilterProps> = ({ attributes, initialLabel = 'Display If' }) => {
  const { data: attributesList, isLoading, isFetching, isError } = useQuery<IAttribute[]>({
    queryKey: ['attributes'],
    queryFn: async (): Promise<IAttribute[]> => {
      const result = await api.get('/api/attributes/', { params: { pageSize: 1000 } })
      return result.data
    },
    enabled: true,
    staleTime: 10000
  })

  const { setValue, control, watch } = useFormContext()
  const { fields, append, remove } = useFieldArray({ control, name: 'courseFilters' })

  const handleAddFilter = useCallback(() => {
    append({ key: null, op: '', value: null })
  }, [append])

  const handleKeyChange = useCallback((index: number, newValue: IAttribute | null) => {
    if (!attributesList) {
      return
    }

    setValue(`courseFilters[${index}].key`, newValue)
    setValue(`courseFilters[${index}].value`, null)
  }, [setValue, attributesList])

  const handleRemoveFilter = useCallback((index: number) => { remove(index) }, [remove])

  useEffect(() => {
    if (attributes && attributes.length > 0) {
      const defaultFilters = attributes.map((attr) => ({
        key: {
          attributeKey: attr.key,
          attributeType: attr.type,
          attributeValues: attributesList?.find((attribute) => attribute.attributeKey === attr.key)?.attributeValues
        },
        op: attr.op,
        value: attr.values
      }))
      setValue('courseFilters', defaultFilters)
    }
  }, [attributes, attributesList, setValue, isFetching, isLoading])

  const filterRow = (item: Partial<TemplateAttributesType>, index: number): JSX.Element => (
    <Grid key={item.id} container spacing={2} justifyContent="center" alignItems="center">
      <Grid item xs={12} sm={2} md={2}>
        <Typography fontWeight={600} width="100%" align="right" mt={-6}>
          {index === 0 ? initialLabel : 'And'}
        </Typography>
      </Grid>
      <Grid item xs={12} sm={3} md={3}>
        <GenericAutocomplete
          control={control}
          options={attributesList}
          loading={isFetching || isLoading}
          placeholder="Attribute"
          name={`courseFilters[${index}].key`}
          getOptionLabel={(option: IAttribute) => option?.attributeKey}
          getOptionValue={(option: IAttribute) => option}
          onChange={(newValue) => { handleKeyChange(index, newValue) }}
        />
      </Grid>
      <Grid item xs={12} sm={3} md={3}>
        <GenericAutocomplete
          control={control}
          options={operations}
          placeholder="is"
          name={`courseFilters[${index}].op`}
          getOptionLabel={(option) => option?.title}
          getOptionValue={(option) => option?.value}
          fullWidth
        />
      </Grid>
      <Grid item xs={12} sm={3} md={3}>
        <MultiSelectWithCheckBox
          name={`courseFilters[${index}].value`}
          options={watch(`courseFilters[${index}].key`)?.attributeValues ?? []}
          placeholder="Values"
          getOptionLabel={(option: string) => option}
          getOptionValue={(option: string) => option}
          loading={isFetching || isLoading}
          fullWidth
        />
      </Grid>
      <Grid item xs={12} sm={1} md={1} mt={-6}>
        <GenericButton
          icon={<DeleteIcon />}
          buttonSize={ButtonSize.Medium}
          buttonType={ButtonType.Ghost}
          style={{ background: 'transparent' }}
          onClick={() => { handleRemoveFilter(index) }}
        />
      </Grid>
    </Grid>
  )

  const renderContent = (): JSX.Element => {
    if (isFetching || isLoading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={'100px'}>
          <CircularProgress />
        </Box>
      )
    }

    if (isError) {
      return (
        <Typography color="error" align="center">
          Failed to load attributes
        </Typography>
      )
    }

    return (
      <Box display="flex" flexDirection="column" gap={2}>
        {fields.map((item, index) => filterRow(item, index))}
        <Box width="100%" display="flex" justifyContent={fields.length === 0 ? 'center' : 'flex-end'}>
          <GenericButton
            label="Condition"
            buttonSize={ButtonSize.Medium}
            buttonType={fields.length === 0 ? ButtonType.Secondary : ButtonType.Ghost}
            startIcon={<AddIcon />}
            onClick={handleAddFilter}
            style={{ background: 'transparent' }}
          />
        </Box>
      </Box>
    )
  }

  return (
    <Box>
      <Typography color='#000' fontWeight={'bold'} mb={5} mt={4}>Display Condition</Typography>

      <Box
        mt={4}
        display="flex"
        flexDirection="column"
        justifyContent="center"
        border={`1px solid ${shade[650]}`}
        padding="24px 16px"
        borderRadius="8px"
        bgcolor={shade[750]}
      >
        {renderContent()}
      </Box>
    </Box>

  )
}

export default AttributesFilter
